import type { DifficultyId, DifficultyPreset } from "./Difficulty";

export type DamageAggressionDifficultyId = Extract<DifficultyId, "liar" | "rakus" | "buang_tebiat">;

export interface DamageAggressionTuning {
  readonly durationSeconds: number;
  readonly movementSpeedMultiplier: number;
  readonly attackCooldownMultiplier: number;
  readonly pursuitReplanMultiplier: number;
  readonly steeringMultiplier: number;
  readonly retreatChance: number;
  readonly frenzyMovementSpeedMultiplier: number;
  readonly frenzyAttackCooldownMultiplier: number;
  readonly frenzyPursuitReplanMultiplier: number;
  readonly frenzySteeringMultiplier: number;
}

/**
 * Post-damage combat tuning. Values replace the normal difficulty cadence while
 * active, so movement and attack multipliers are never multiplied twice.
 */
export const DAMAGE_AGGRESSION_TUNING: Readonly<Record<DamageAggressionDifficultyId, DamageAggressionTuning>> = {
  liar: {
    durationSeconds: 8,
    movementSpeedMultiplier: 1.2,
    attackCooldownMultiplier: .8,
    pursuitReplanMultiplier: .95,
    steeringMultiplier: 1.1,
    retreatChance: .25,
    frenzyMovementSpeedMultiplier: 1.2,
    frenzyAttackCooldownMultiplier: .8,
    frenzyPursuitReplanMultiplier: .95,
    frenzySteeringMultiplier: 1.1,
  },
  rakus: {
    durationSeconds: 12,
    movementSpeedMultiplier: 1.4,
    attackCooldownMultiplier: .65,
    pursuitReplanMultiplier: .75,
    steeringMultiplier: 1.25,
    retreatChance: .1,
    frenzyMovementSpeedMultiplier: 1.5,
    frenzyAttackCooldownMultiplier: .58,
    frenzyPursuitReplanMultiplier: .62,
    frenzySteeringMultiplier: 1.4,
  },
  buang_tebiat: {
    durationSeconds: 18,
    // Keep the existing 1.70 difficulty floor instead of slowing it to 1.65.
    movementSpeedMultiplier: 1.7,
    attackCooldownMultiplier: .5,
    pursuitReplanMultiplier: .58,
    steeringMultiplier: 1.45,
    retreatChance: .03,
    frenzyMovementSpeedMultiplier: 1.85,
    frenzyAttackCooldownMultiplier: .42,
    frenzyPursuitReplanMultiplier: .45,
    frenzySteeringMultiplier: 1.7,
  },
};

export function damageAggressionTuning(difficulty: DifficultyPreset | DifficultyId): DamageAggressionTuning | undefined {
  const id = typeof difficulty === "string" ? difficulty : difficulty.id;
  return id === "liar" || id === "rakus" || id === "buang_tebiat" ? DAMAGE_AGGRESSION_TUNING[id] : undefined;
}

/** One controller-owned timer; it advances only from the normal game update. */
export class DamageAggression {
  private remaining = 0;
  private hitCount = 0;
  private frenzyActive = false;
  private readonly processedHitIds = new Set<string>();
  private readonly tuning?: DamageAggressionTuning;

  constructor(private readonly difficulty: DifficultyPreset) {
    this.tuning = damageAggressionTuning(difficulty);
  }

  get active(): boolean { return Boolean(this.tuning) && this.remaining > 0; }
  get frenzy(): boolean { return this.active && this.frenzyActive; }
  get remainingSeconds(): number { return Math.max(0, this.remaining); }
  get successfulHits(): number { return this.active ? this.hitCount : 0; }
  get movementSpeedMultiplier(): number {
    if (!this.active || !this.tuning) return this.difficulty.movementSpeedMultiplier;
    return this.frenzy ? this.tuning.frenzyMovementSpeedMultiplier : this.tuning.movementSpeedMultiplier;
  }
  get attackCooldownMultiplier(): number {
    if (!this.active || !this.tuning) return this.difficulty.attackCooldownMultiplier;
    return this.frenzy ? this.tuning.frenzyAttackCooldownMultiplier : this.tuning.attackCooldownMultiplier;
  }
  get pursuitReplanMultiplier(): number {
    if (!this.active || !this.tuning) return 1;
    return this.frenzy ? this.tuning.frenzyPursuitReplanMultiplier : this.tuning.pursuitReplanMultiplier;
  }
  get steeringMultiplier(): number {
    if (!this.active || !this.tuning) return 1;
    return this.frenzy ? this.tuning.frenzySteeringMultiplier : this.tuning.steeringMultiplier;
  }

  registerEffectiveHit(hitId: string): boolean {
    if (!this.tuning || this.processedHitIds.has(hitId)) return false;
    this.processedHitIds.add(hitId);
    this.hitCount = this.active ? this.hitCount + 1 : 1;
    this.remaining = this.tuning.durationSeconds;
    this.frenzyActive = this.difficulty.id !== "liar" && this.hitCount >= 2;
    return true;
  }

  update(deltaSeconds: number): void {
    if (!this.active) return;
    this.remaining = Math.max(0, this.remaining - Math.max(0, deltaSeconds));
    if (this.remaining === 0) { this.hitCount = 0; this.frenzyActive = false; }
  }

  reset(): void {
    this.remaining = 0;
    this.hitCount = 0;
    this.frenzyActive = false;
    this.processedHitIds.clear();
  }
}
